/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { createSelector } from "@reduxjs/toolkit";
import { AppState } from "../../";
import { encode as base64Encode } from "base-64";
import { IfilterObj } from "../types";
import { constStr } from "../../../globals/constants";
import {
  deviceStatusSelector,
  deviceMaintenanceModeSelector,
  deviceKpiSyncSelector,
  deviceConfigStatusSelector,
  deviceSiteGroupSelector,
  deviceRegionFilterIdsSelector,
} from "../Nodes/selectors";



// Basic selectors
export const inventoryStatusSelector = (state: AppState) => state.inventoryFilter?.selectedStatus;
export const inventoryStateSelector = (state: AppState) => state.inventoryFilter?.selectedState;
export const inventoryModelSelector = (state: AppState) => state.inventoryFilter?.selectedModel;
export const inventoryParentSelector = (state: AppState) => state.inventoryFilter?.selectedParent;
export const inventoryTechnologySelector = (state: AppState) => state.inventoryFilter?.selectedTechnology;
export const inventorySearchKeywordSelector = (state: AppState) => state.inventoryFilter?.searchKeyword;
export const inventoryViewSelector = (state: AppState) => state.inventoryFilter?.selectedView || "radionodes";
export const inventoryRegionFilterIdsSelector = (state: AppState) => state.inventoryFilter?.regionFilterIds || [];

// Selector from admin state for GeoRegion integration
export const selectedGeoRegionSelector = (state: AppState) => state.admin?.selectedGeoRegion?.name || "";
export const selectedGeoRegionIdsSelector = (state: AppState) => state.admin?.selectedGeoRegionIds || [];

/**
 * URL Parameter Selector for RadioNodes
 * Builds Base64-encoded filter predicates following the same pattern as Nodes
 */
export const inventoryRadioNodesUrlParameterSelector = createSelector(
  inventoryStatusSelector,
  inventoryStateSelector,
  inventoryModelSelector,
  inventoryParentSelector,
  inventoryRegionFilterIdsSelector,
  selectedGeoRegionIdsSelector,
  deviceStatusSelector,
  deviceMaintenanceModeSelector,
  deviceKpiSyncSelector,
  deviceConfigStatusSelector,
  deviceSiteGroupSelector,
  deviceRegionFilterIdsSelector,
  (status, state, model, parent, regionFilterIds, geoRegionIds, navDeviceStatus, navMaintenanceMode, navPmCollected, navConfigStatus, navSiteGroup, navRegionFilterIds) => {
    const params = new URLSearchParams();
    const filterObj: IfilterObj = { type: "AND", operator: "", fieldName: "", value: "", predicates: [] };

    // Status filter from inventory dropdown: connected / disconnected
    // Sent as an "operState" predicate in the filter JSON; the backend
    // InventoryFilterBuilder intercepts it and applies the correct
    // remoteequipment.STATUS IN ('OOS-NOTPRESENT', 'UNKNOWN') logic.
    if (status && status !== "" && status !== "all") {
      filterObj.predicates.push({ type: "", operator: "EQUAL", fieldName: "operState", value: status, predicates: [{}] });
    }
    if (navDeviceStatus) {
      filterObj.predicates.push({ type: "", operator: "EQUAL", fieldName: "status", value: navDeviceStatus, predicates: [{}] });
    }

    // Nav panel: In Maintenance
    if (navMaintenanceMode) {
      filterObj.predicates.push({
        type: "",
        operator: navMaintenanceMode === "true" ? "is_true" : "is_false",
        fieldName: "maintenanceMode",
        value: "",
        predicates: [{}],
      });
    }

    // Nav panel: KPI Sync Errors (pmCollected = false)
    if (navPmCollected) {
      filterObj.predicates.push({
        type: "",
        operator: navPmCollected === "true" ? "is_true" : "is_false",
        fieldName: "pmCollected",
        value: "",
        predicates: [{}],
      });
    }

    // Nav panel: Offline / Config Mismatch (configurationStatus on servicesdeviceview)
    if (navConfigStatus) {
      if (navConfigStatus === "Mismatch") {
        filterObj.predicates.push({ type: "", operator: "NE", fieldName: "goldenAuditFailCount", value: "0", predicates: [{}] });
      } else {
        filterObj.predicates.push({ type: "", operator: "EQUAL", fieldName: "configurationStatus", value: navConfigStatus, predicates: [{}] });
      }
    }

    // Nav panel: Ungrouped (siteGroupId LIKE '99999999%' on servicesdeviceview)
    if (navSiteGroup === constStr.ungrouped) {
      filterObj.predicates.push({ type: "", operator: "LIKE", fieldName: "siteGroupId", value: "99999999%", predicates: [{}] });
    }

    // Model filter
    if (model) {
      filterObj.predicates.push({ type: "", operator: "EQUAL", fieldName: "model", value: model, predicates: [{}] });
    }

    // State filter (RemoteEquipment state values)
    // NOTE: uses fieldName "operState" (not "status") so the backend routes it to
    // remoteequipment.STATUS (string oper-state) instead of servicesdeviceview.status (int).
    if (state) {
      filterObj.predicates.push({ type: "", operator: "EQUAL", fieldName: "operState", value: state, predicates: [{}] });
    }

    // Parent filter
    if (parent) {
      filterObj.predicates.push({ type: "", operator: "EQUAL", fieldName: "parentKey", value: parent, predicates: [{}] });
    }

    // GeoRegion filter — priority: inventory dropdown > left-nav region > global user regions
    // Skip when ungrouped is selected: siteGroupId predicate already scopes the results
    if (navSiteGroup !== constStr.ungrouped) {
      const effectiveRegionIds =
        regionFilterIds && regionFilterIds.length > 0
          ? regionFilterIds
          : navRegionFilterIds && navRegionFilterIds.length > 0
          ? navRegionFilterIds
          : geoRegionIds;
      if (effectiveRegionIds && effectiveRegionIds.length > 0) {
        filterObj.predicates.push({
          type: "",
          operator: "IN",
          fieldName: "geoRegionId",
          value: effectiveRegionIds.join(","),
          predicates: [{}],
        });
      }
    }

    // Base64 encode if there are predicates
    if (filterObj.predicates.length > 0) {
      const base64EncodedStr = base64Encode(JSON.stringify(filterObj));
      params.append("filter", base64EncodedStr);
    }

    return params;
  }
);

/**
 * URL Parameter Selector for Cells
 * Builds Base64-encoded filter predicates for cells
 */
export const inventoryCellsUrlParameterSelector = createSelector(
  inventoryStatusSelector,
  inventoryStateSelector,
  inventoryTechnologySelector,
  inventoryRegionFilterIdsSelector,
  selectedGeoRegionIdsSelector,
  deviceStatusSelector,
  deviceMaintenanceModeSelector,
  deviceKpiSyncSelector,
  deviceConfigStatusSelector,
  deviceSiteGroupSelector,
  deviceRegionFilterIdsSelector,
  (status, state, technology, regionFilterIds, geoRegionIds, navDeviceStatus, navMaintenanceMode, navPmCollected, navConfigStatus, navSiteGroup, navRegionFilterIds) => {
    const params = new URLSearchParams();
    const filterObj: IfilterObj = { type: "AND", operator: "", fieldName: "", value: "", predicates: [] };

    // Status filter from inventory dropdown: connected / disconnected
    // Sent as an "operState" predicate in the filter JSON; the backend
    // InventoryFilterBuilder intercepts it and applies the correct
    // remoteequipment.STATUS IN ('OOS-NOTPRESENT', 'UNKNOWN') logic.
    if (status && status !== "" && status !== "all") {
      filterObj.predicates.push({ type: "", operator: "EQUAL", fieldName: "operState", value: status, predicates: [{}] });
    }

    // Nav panel: Disconnected
    if (navDeviceStatus) {
      filterObj.predicates.push({ type: "", operator: "EQUAL", fieldName: "status", value: navDeviceStatus, predicates: [{}] });
    }

    // Nav panel: In Maintenance
    if (navMaintenanceMode) {
      filterObj.predicates.push({
        type: "",
        operator: navMaintenanceMode === "true" ? "is_true" : "is_false",
        fieldName: "maintenanceMode",
        value: "",
        predicates: [{}],
      });
    }

    // Nav panel: KPI Sync Errors
    if (navPmCollected) {
      filterObj.predicates.push({
        type: "",
        operator: navPmCollected === "true" ? "is_true" : "is_false",
        fieldName: "pmCollected",
        value: "",
        predicates: [{}],
      });
    }

    // Nav panel: Offline / Config Mismatch
    if (navConfigStatus) {
      if (navConfigStatus === "Mismatch") {
        filterObj.predicates.push({ type: "", operator: "NE", fieldName: "goldenAuditFailCount", value: "0", predicates: [{}] });
      } else {
        filterObj.predicates.push({ type: "", operator: "EQUAL", fieldName: "configurationStatus", value: navConfigStatus, predicates: [{}] });
      }
    }

    // Nav panel: Ungrouped
    if (navSiteGroup === constStr.ungrouped) {
      filterObj.predicates.push({ type: "", operator: "LIKE", fieldName: "siteGroupId", value: "99999999%", predicates: [{}] });
    }

    // Technology filter (LTECell or NRCell)
    if (technology) {
      filterObj.predicates.push({ type: "", operator: "EQUAL", fieldName: "equipmentType", value: technology, predicates: [{}] });
    }

    // State filter (RemoteEquipment state values)
    // NOTE: uses fieldName "operState" (not "status") so the backend routes it to
    // remoteequipment.STATUS (string oper-state) instead of servicesdeviceview.status (int).
    if (state) {
      filterObj.predicates.push({ type: "", operator: "EQUAL", fieldName: "operState", value: state, predicates: [{}] });
    }

    // GeoRegion filter — priority: inventory dropdown > left-nav region > global user regions
    // Skip when ungrouped is selected: siteGroupId predicate already scopes the results
    if (navSiteGroup !== constStr.ungrouped) {
      const effectiveRegionIds =
        regionFilterIds && regionFilterIds.length > 0
          ? regionFilterIds
          : navRegionFilterIds && navRegionFilterIds.length > 0
          ? navRegionFilterIds
          : geoRegionIds;
      if (effectiveRegionIds && effectiveRegionIds.length > 0) {
        filterObj.predicates.push({
          type: "",
          operator: "IN",
          fieldName: "geoRegionId",
          value: effectiveRegionIds.join(","),
          predicates: [{}],
        });
      }
    }

    // Base64 encode if there are predicates
    if (filterObj.predicates.length > 0) {
      const base64EncodedStr = base64Encode(JSON.stringify(filterObj));
      params.append("filter", base64EncodedStr);
    }

    return params;
  }
);
